import * as React from 'react';
import * as ReactIs from 'react-is';

import {ClassNameBuilder} from '../../utils/ClassNameBuilder';

import * as Styles from './ListGroupItem.module.scss';
import {ListItemProps, ListContext} from './ListGroup';

export interface ListGroupItemProps<T> extends ListItemProps<T> {
    activeRef?: React.MutableRefObject<HTMLElement | null>;
    className?: string;
    itemActiveBackground?: boolean;
    onSelect?: (item: T) => void;
    onHighlight?: (item: T) => void;
    /** Do not set this prop. It is set by the ListGroup component. */
    index?: number;
    style?: React.CSSProperties;
    border?: boolean;
    autoScroll?: boolean;
    horizontal?: boolean;
    children: React.ReactNode;
}

export const ListGroupItem = <T extends {}>(props: ListGroupItemProps<T>) => {
    const {
        children,
        autoScroll = true,
        border = true,
        horizontal = false,
        className,
        itemActiveBackground = true,
        style,
        activeRef = undefined,
        item,
        index,
        onSelect,
        onHighlight
    } = props;
    const listContext = React.useContext(ListContext);
    const classNames = new ClassNameBuilder(Styles, 'list-group-item')
        .add(className, undefined)
        .add(['border', border])
        .add(['horizontal', horizontal])
        .add(['no-background', !itemActiveBackground])
        .add(['list-group-item-active', !!activeRef]);
    // Pass a reference to the child of this element so we can trigger a click
    // if `clickChild` is true.
    const childRef = React.useRef<HTMLElement>();
    // When this ListGroupItem is active (keyboard/mouse)
    const activeRefFunc = React.useCallback((node: HTMLLIElement) => {
        if (node) {
            if (activeRef) {
                activeRef.current = node;

                if (autoScroll) {
                    node.scrollIntoView({
                        behavior: 'auto',
                        block: 'nearest'
                    });
                }
                onHighlight?.(item);
            }
        }
    }, [activeRef]);

    const handleClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        listContext.setValue(index!);
        if (!event.isTrusted) {
            childRef.current?.click();
        }
        onSelect?.(item);
    };

    const handleMouseOver = (event: React.MouseEvent) => {
        event.stopPropagation();
        // When mousing over the ListGroup's highlightedIndex gets this index
        // This updates the activeRef to be this component and the useEffect
        // triggers the onHighlight
        listContext.setValue(index!);
    };

    return (
        <li className={classNames.toString()} onMouseEnter={handleMouseOver} ref={activeRefFunc} style={style} onClick={handleClick}>
            {
                ReactIs.isElement(children)
                    ? ReactIs.isForwardRef(children)
                        ? React.cloneElement(children as React.ReactElement, {ref: childRef})
                        : children
                    : children
            }
        </li>
    );
};
